import app from 'flarum/forum/app';
import { override } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import Icon from 'flarum/common/components/Icon';

/**
 * fof/upload's media manager ("Мои загрузки" in the editor) as a plain media library:
 *   МЕДИАТЕКА + one line of what it is, the upload button on the right, two quiet tabs,
 *   a grid of equal square previews with the name and "PNG · 5 kB" under each, secondary
 *   actions (hide, delete) small in a corner, a gold frame and ✓ on what is selected.
 * fof/upload's components, state, requests and permissions stay as they are: only the
 * rendered tree gets a title, a lead line, file meta, a check mark and an empty state;
 * the layout is less/forum/media.less, scoped to .fof-file-manager-modal.
 */
const t = function (key, params) {
  if (params === void 0) {
    params = {};
  }
  return app.translator.trans(`tarotportal-theme.forum.media.${key}`, params);
};
const hasClass = (vnode, name) => typeof vnode?.attrs?.className === 'string' && vnode.attrs.className.split(/\s+/).includes(name);

/** Depth-first search for an element vnode with the given class (component vnodes are not entered). */
function find(vnode, name) {
  if (!vnode || typeof vnode !== 'object') return null;
  if (Array.isArray(vnode)) {
    for (const child of vnode) {
      const found = find(child, name);
      if (found) return found;
    }
    return null;
  }
  if (hasClass(vnode, name)) return vnode;
  return typeof vnode.tag === 'string' || vnode.tag === '[' ? find(vnode.children, name) : null;
}

/** "image/png" → "PNG", "application/pdf" → "PDF". */
const typeLabel = mime => (mime.split('/')[1] || mime).replace(/^x-/, '').split(/[+.;]/)[0].toUpperCase();
export default function addMediaManager() {
  // Head: title and lead line instead of the "Менеджер файлов" heading
  override('ext:fof/upload/forum/components/FileManagerModal', 'view', function (original) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    const vnode = original(...args);
    const header = find(vnode, 'Modal-header');
    if (header && Array.isArray(header.children)) {
      // flat: the children of a rendered vnode are already normalised (no nested arrays)
      header.children = header.children.flatMap(child => child?.tag === 'h3' ? [m("h3", {
        className: "TpMedia-title"
      }, t('title')), m("p", {
        className: "TpMedia-lead"
      }, t('lead'))] : child);
    }
    return vnode;
  });

  // Each file: "PNG · 5 kB" under the name (fof/upload's own data) and a check mark when selected
  override('ext:fof/upload/common/components/DisplayFile', 'view', function (original) {
    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
      args[_key2 - 1] = arguments[_key2];
    }
    const vnode = original(...args);
    if (!vnode || !Array.isArray(vnode.children)) return vnode;
    const file = this.file;
    const meta = [typeLabel(String(file.type() || '')), file.humanSize()].filter(Boolean).join(' · ');
    vnode.children = [...vnode.children, meta ? m("div", {
      className: "TpFileMeta"
    }, meta) : null, this.isSelected ? m("span", {
      className: "TpFileCheck",
      "aria-hidden": "true"
    }, m(Icon, {
      name: "fas fa-check"
    })) : null];
    vnode.attrs = {
      ...vnode.attrs,
      'aria-pressed': this.isSelected ? 'true' : 'false',
      title: file.baseName()
    };
    return vnode;
  });

  // Empty library: what will appear here, and (own uploads, upload allowed) the same upload button's file picker
  override('ext:fof/upload/common/components/AbstractFIleList', 'view', function (original) {
    for (var _len3 = arguments.length, args = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
      args[_key3 - 1] = arguments[_key3];
    }
    const vnode = original(...args);
    const placeholder = find(vnode, 'Placeholder');
    if (!placeholder || this.isLoading() || this.fileState?.hasLoadError?.()) return vnode;
    const shared = !!this.fileState?.sharedFiles;
    const canUpload = !shared && this.inModal && !!app.forum.attribute('fof-upload.canUpload');
    placeholder.attrs = {
      ...placeholder.attrs,
      className: 'Placeholder TpMediaEmpty'
    };
    placeholder.children = [m("p", {
      className: "TpMediaEmpty-title"
    }, t(shared ? 'empty_shared_title' : 'empty_title')), m("p", {
      className: "TpMediaEmpty-text"
    }, t(shared ? 'empty_shared_text' : 'empty_text')), canUpload ? m(Button, {
      className: "Button TpMediaEmpty-upload",
      icon: "fas fa-file-upload",
      onclick: () => this.$().closest('.Modal-content').find('.fof-modal-buttons input[type="file"]').trigger('click')
    }, t('empty_upload')) : null];
    delete placeholder.text;
    return vnode;
  });
}